import { useState, useEffect, useRef, useCallback } from "react";
import { useApp } from "../context/AppContext";

const QUICK_ACTIONS = [
  { label: "Створити тікет", text: "Створи тікет: " },
  { label: "Додати задачу", text: "Додай задачу на канбан: " },
  { label: "Статистика", text: "Покажи статистику проєкту" },
];

export default function AiAssistant() {
  const { proj, activeId, apiFetch, dark, currentUser } = useApp();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [pulse, setPulse] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevActiveId = useRef(null);

  // Stop pulse after 5 seconds
  useEffect(() => {
    if (pulse) {
      const t = setTimeout(() => setPulse(false), 5000);
      return () => clearTimeout(t);
    }
  }, [pulse]);

  // Reset history when project changes
  useEffect(() => {
    if (activeId !== prevActiveId.current) {
      prevActiveId.current = activeId;
      setMessages([]);
      setHistoryLoaded(false);
    }
  }, [activeId]);

  // Load chat history when opened
  useEffect(() => {
    if (!open || !activeId || historyLoaded) return;
    apiFetch(`/api/projects/${activeId}/assistant/chat/history`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length) {
          setMessages(data.map((m) => ({
            role: m.role,
            text: m.text,
            action: m.action || null,
            ts: m.ts,
          })));
        }
        setHistoryLoaded(true);
      })
      .catch(() => setHistoryLoaded(true));
  }, [open, activeId, historyLoaded, apiFetch]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Focus input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || !activeId) return;

    const userMsg = { role: "user", text, ts: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      // Send last 5 messages as context
      const history = [...messages.slice(-10), userMsg].map((m) => ({
        role: m.role,
        text: m.text,
      }));

      const resp = await apiFetch(`/api/projects/${activeId}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, history: history.slice(-5) }),
      });

      const data = await resp.json();

      if (data.error) {
        setMessages((prev) => [...prev, { role: "assistant", text: data.error, ts: Date.now() }]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", text: data.message, action: data.action, ts: Date.now() },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "Помилка з'єднання з AI. Спробуйте ще раз.", ts: Date.now() },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, activeId, messages, apiFetch]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickAction = (text) => {
    setInput(text);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  if (!activeId) return null;

  // ── Colors ──
  const bg = dark ? "#1e1e2e" : "#ffffff";
  const bgPanel = dark ? "#181825" : "#f8f9fa";
  const bgHeader = dark ? "#313244" : "#7c3aed";
  const bgUser = "#7c3aed";
  const bgAi = dark ? "#313244" : "#f1f3f5";
  const textMain = dark ? "#cdd6f4" : "#1e1e2e";
  const textMuted = dark ? "#6c7086" : "#868e96";
  const textOnPurple = "#ffffff";
  const border = dark ? "#45475a" : "#dee2e6";
  const inputBg = dark ? "#313244" : "#ffffff";
  const actionBadgeBg = dark ? "#1e5631" : "#d3f9d8";
  const actionBadgeText = dark ? "#a6e3a1" : "#2b8a3e";
  const quickBg = dark ? "#313244" : "#f1f0ff";
  const quickText = dark ? "#cba6f7" : "#7c3aed";
  const quickBorder = dark ? "#45475a" : "#e0dbff";

  // ── Floating button ──
  const btnStyle = {
    position: "fixed",
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #7c3aed, #a855f7)",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 4px 20px rgba(124,58,237,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9998,
    transition: "transform 0.2s, box-shadow 0.2s",
    animation: pulse ? "spalahPulse 2s infinite" : "none",
  };

  // ── Panel ──
  const panelStyle = {
    position: "fixed",
    bottom: 90,
    right: 24,
    width: 380,
    height: 500,
    borderRadius: 16,
    background: bg,
    border: `1px solid ${border}`,
    boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
    zIndex: 9999,
    overflow: "hidden",
    animation: "spalahSlideUp 0.25s ease-out",
  };

  const headerStyle = {
    background: bgHeader,
    color: textOnPurple,
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  };

  const messagesStyle = {
    flex: 1,
    overflowY: "auto",
    padding: 12,
    background: bgPanel,
  };

  const inputAreaStyle = {
    padding: "10px 12px",
    borderTop: `1px solid ${border}`,
    background: bg,
    flexShrink: 0,
  };

  return (
    <>
      {/* Keyframes injected once */}
      <style>{`
        @keyframes spalahPulse {
          0% { box-shadow: 0 4px 20px rgba(124,58,237,0.4); }
          50% { box-shadow: 0 4px 30px rgba(124,58,237,0.7), 0 0 0 8px rgba(124,58,237,0.15); }
          100% { box-shadow: 0 4px 20px rgba(124,58,237,0.4); }
        }
        @keyframes spalahSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spalahDots {
          0%, 20% { opacity: 0.3; }
          50% { opacity: 1; }
          80%, 100% { opacity: 0.3; }
        }
        .spalah-ai-scrollbar::-webkit-scrollbar { width: 5px; }
        .spalah-ai-scrollbar::-webkit-scrollbar-thumb { background: ${dark ? "#45475a" : "#ced4da"}; border-radius: 4px; }
        .spalah-ai-scrollbar::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      {/* Floating button */}
      <button
        style={btnStyle}
        onClick={() => setOpen(!open)}
        title="AI Помічник"
        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.08)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a8 8 0 0 1 8 8c0 3.5-2 6-4 7.5V20a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.5C6 16 4 13.5 4 10a8 8 0 0 1 8-8z" />
            <path d="M9 22h6" />
            <circle cx="10" cy="10" r="1" fill="currentColor" />
            <circle cx="14" cy="10" r="1" fill="currentColor" />
          </svg>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={panelStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a8 8 0 0 1 8 8c0 3.5-2 6-4 7.5V20a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.5C6 16 4 13.5 4 10a8 8 0 0 1 8-8z" />
                <circle cx="10" cy="10" r="1" fill="currentColor" />
                <circle cx="14" cy="10" r="1" fill="currentColor" />
              </svg>
              <span style={{ fontWeight: 600, fontSize: 15 }}>AI Помічник</span>
              {proj?.name && (
                <span style={{ fontSize: 11, opacity: 0.75, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  — {proj.name}
                </span>
              )}
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: "none", border: "none", color: textOnPurple, cursor: "pointer", padding: 4, borderRadius: 6, display: "flex" }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Messages */}
          <div style={messagesStyle} className="spalah-ai-scrollbar">
            {messages.length === 0 && !loading && (
              <div style={{ textAlign: "center", padding: "40px 20px", color: textMuted }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12, opacity: 0.5 }}>
                  <path d="M12 2a8 8 0 0 1 8 8c0 3.5-2 6-4 7.5V20a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.5C6 16 4 13.5 4 10a8 8 0 0 1 8-8z" />
                  <circle cx="10" cy="10" r="1" fill={textMuted} />
                  <circle cx="14" cy="10" r="1" fill={textMuted} />
                </svg>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Привіт! Я Spalah AI</div>
                <div style={{ fontSize: 12 }}>Запитай про проєкт, створи тікет або задачу</div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", marginBottom: 8 }}>
                <div
                  style={{
                    maxWidth: "82%",
                    padding: "8px 12px",
                    borderRadius: msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                    background: msg.role === "user" ? bgUser : bgAi,
                    color: msg.role === "user" ? textOnPurple : textMain,
                    fontSize: 13,
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    border: msg.role === "user" ? "none" : `1px solid ${border}`,
                  }}
                >
                  {msg.text}

                  {/* Action badge */}
                  {msg.action && msg.action.success && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "5px 10px",
                        borderRadius: 8,
                        background: actionBadgeBg,
                        color: actionBadgeText,
                        fontSize: 12,
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      {msg.action.label || "Виконано"}
                    </div>
                  )}

                  {msg.action && msg.action.success === false && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "5px 10px",
                        borderRadius: 8,
                        background: dark ? "#4a1e1e" : "#ffe0e0",
                        color: dark ? "#f38ba8" : "#c92a2a",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Помилка: {msg.action.error || "невідома"}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8 }}>
                <div style={{ padding: "10px 16px", borderRadius: "14px 14px 14px 4px", background: bgAi, border: `1px solid ${border}`, display: "flex", gap: 4 }}>
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: textMuted,
                        display: "inline-block",
                        animation: `spalahDots 1.4s infinite ${d * 0.2}s`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick actions */}
          <div style={{ padding: "6px 12px 2px", display: "flex", gap: 6, flexWrap: "wrap", background: bg, borderTop: `1px solid ${border}` }}>
            {QUICK_ACTIONS.map((qa) => (
              <button
                key={qa.label}
                onClick={() => handleQuickAction(qa.text)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 20,
                  border: `1px solid ${quickBorder}`,
                  background: quickBg,
                  color: quickText,
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "opacity 0.15s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.75"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              >
                {qa.label}
              </button>
            ))}
          </div>

          {/* Input area */}
          <div style={inputAreaStyle}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Напишіть повідомлення..."
                rows={1}
                style={{
                  flex: 1,
                  resize: "none",
                  border: `1px solid ${border}`,
                  borderRadius: 10,
                  padding: "8px 12px",
                  fontSize: 13,
                  lineHeight: 1.4,
                  background: inputBg,
                  color: textMain,
                  outline: "none",
                  maxHeight: 80,
                  fontFamily: "inherit",
                  transition: "border-color 0.15s",
                }}
                onFocus={(e) => { e.target.style.borderColor = "#7c3aed"; }}
                onBlur={(e) => { e.target.style.borderColor = border; }}
                onInput={(e) => {
                  e.target.style.height = "auto";
                  e.target.style.height = Math.min(e.target.scrollHeight, 80) + "px";
                }}
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: "none",
                  background: loading || !input.trim() ? (dark ? "#45475a" : "#dee2e6") : "#7c3aed",
                  color: loading || !input.trim() ? textMuted : "#fff",
                  cursor: loading || !input.trim() ? "default" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "background 0.15s",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13" />
                  <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
